import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';
import {
  AttributeEditor,
  Box,
  Button,
  ColumnLayout,
  Container,
  Flashbar,
  FlashbarProps,
  Header,
  Input,
  Modal,
  NonCancelableCustomEvent,
  SpaceBetween,
  Spinner,
} from '@amzn/awsui-components-react-v3';
import { editDataLakeRole, listDataLakeRoleProperty } from 'src/api/permissions';
import * as validate from '../../../commons/validationUtils';
import { BaseChangeDetail } from '@amzn/awsui-components-react-v3/polaris/input/interfaces';
import { TABLE_CONTENT_TYPE } from 'src/commons/constants';
import { enableIamBasedAccess } from 'src/api/config';
import { ListDataLakeRolePropertyResult } from 'aws-sdk/clients/awsdatalakegladstonelambda';

export interface TrustedIAMPrincipalsProps {
  setContentType: any;
  groupInfo: any;
  activeGroup: string;
  username: string;
}

const TrustedIAMPrincipals: React.FC<TrustedIAMPrincipalsProps> = (props: TrustedIAMPrincipalsProps) => {
  const [trustedEntities, setTrustedEntities] = useState<string[]>([]);
  const [trustedEntitiesOld, setTrustedEntitiesOld] = useState<string[]>([]);
  const [pendingEntities, setPendingEntities] = useState<string[]>([]);
  const [addedEntities, setAddedEntities] = useState<string[]>([]);
  const [removedEntities, setRemovedEntities] = useState<string[]>([]);
  const [editingEntities, setEditingEntities] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [noPrincipalsModalVisible, setNoPrincipalsModalVisible] = useState(false);
  const [invalidArnsModalVisible, setInvalidArnsModalVisible] = useState(false);
  const [invalidArns, setInvalidArns] = useState<string[]>([]);
  const [notifications, setNotifications] = useState<FlashbarProps.MessageDefinition[]>([]);
  const [loadingRoleProperties, setLoadingRoleProperties] = useState(true);
  const [loadingEditRoleRequest, setLoadingEditRoleRequest] = useState(false);

  const onAddButtonClickHandler = () => {
    setTrustedEntities([...trustedEntities, '']);
  };

  const onRemoveButtonClickHandler = ({ detail: { itemIndex } }: any) => {
    const newTrustedEntities = trustedEntities.slice();
    newTrustedEntities.splice(itemIndex, 1);
    setTrustedEntities(newTrustedEntities);
  };

  const getOnInputHandler = (e: NonCancelableCustomEvent<BaseChangeDetail>, index: number) => {
    const {
      detail: { value },
    } = e;
    const newTrustedEntities = [...trustedEntities];
    newTrustedEntities[index] = value;
    setTrustedEntities(newTrustedEntities);
  };

  const edit = () => {
    setTrustedEntitiesOld([...trustedEntities]);
    setEditingEntities(true);
  };

  const cancel = () => {
    setTrustedEntities([...trustedEntitiesOld]);
    setEditingEntities(false);
  };

  const save = () => {
    const differences = processEdits(trustedEntitiesOld, trustedEntities);

    const newAddedEntities = differences.adds;
    const newRemovedEntities = differences.removes;

    setAddedEntities(newAddedEntities);
    setRemovedEntities(newRemovedEntities);

    const newInvalidArns: string[] = [];
    newAddedEntities.forEach((e) => {
      if (!validate.isValidPrincipal(e)) {
        newInvalidArns.push(e);
      }
    });
    setInvalidArns(newInvalidArns);

    // IAM has a rule that a role cannot have 0 principals. Because removes go
    // through immediately and adds require approval, some edits are not possible.
    if (newRemovedEntities.length === trustedEntitiesOld.length && newRemovedEntities.length > 0) {
      setNoPrincipalsModalVisible(true);
    } else if (newInvalidArns.length > 0) {
      setInvalidArnsModalVisible(true);
    } else {
      setModalVisible(true);
    }
  };

  const saveButtonDisabled = () => {
    const differences = processEdits(trustedEntitiesOld, trustedEntities);

    if (differences.adds.length === 0 && differences.removes.length === 0) {
      return true;
    }

    return trustedEntities.some((e) => e === '');
  };

  // compares the lists of entities before and after edits
  // and returns list of those added and those removed
  const processEdits = (oldEntities: string[], newEntities: string[]) => {
    const adds: string[] = [];
    const removes: string[] = [];

    newEntities.forEach((e) => {
      if (!oldEntities.includes(e)) {
        adds.push(e);
      }
    });

    oldEntities.forEach((e) => {
      if (!newEntities.includes(e)) {
        removes.push(e);
      }
    });

    return { adds, removes };
  };

  const closeModal = () => {
    setModalVisible(false);
    setLoadingEditRoleRequest(false);
  };

  const submitModal = async () => {
    setLoadingEditRoleRequest(true);
    let updateFailed = false;
    try {
      await editDataLakeRole({
        groupId: props.activeGroup,
        principalsToAdd: addedEntities,
        principalsToDelete: removedEntities,
      });
    } catch (err) {
      updateFailed = true;
    }

    closeModal();
    cancel();

    if (updateFailed) {
      setNotifications([
        {
          type: 'error',
          content: 'Failed to update properties for group: ' + props.activeGroup,
          dismissible: true,
          onDismiss: () => setNotifications([]),
        },
      ]);
    } else {
      setNotifications([
        {
          type: 'success',
          content: 'Successfully updated properties for group: ' + props.activeGroup,
          dismissible: true,
          onDismiss: () => setNotifications([]),
        },
      ]);
    }
    await loadPermissions();
  };

  const closeNoPrincipalsModal = () => {
    setNoPrincipalsModalVisible(false);
  };

  const closeInvalidArnsModal = () => {
    setInvalidArnsModalVisible(false);
  };

  const loadPermissions = useCallback(async () => {
    setLoadingRoleProperties(true);
    let roleProperties: ListDataLakeRolePropertyResult;
    try {
      roleProperties = await listDataLakeRoleProperty({
        groupId: props.activeGroup,
      });
    } catch (err) {
      setNotifications([
        {
          type: 'error',
          content: 'Failed to get properties for group: ' + props.activeGroup,
          dismissible: true,
          onDismiss: () => setNotifications([]),
        },
      ]);
      return;
    }

    setTrustedEntities(roleProperties.trustedEntities);
    setPendingEntities(roleProperties.pendingPrincipals);
    setLoadingRoleProperties(false);
  }, [props.activeGroup]);

  useEffect(() => {
    props.setContentType(TABLE_CONTENT_TYPE);
    loadPermissions();
  }, []);

  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  if (loadingRoleProperties || loadingEditRoleRequest) {
    return <Spinner />;
  }

  return (
    <div>
      <Flashbar items={notifications} />
      <Modal
        visible={modalVisible}
        header='Confirm edits'
        onDismiss={closeModal}
        footer={
          <Box float='right'>
            <SpaceBetween direction='horizontal' size='xs'>
              <Button variant='link' disabled={loadingEditRoleRequest} onClick={closeModal}>
                No
              </Button>
              <Button
                variant='primary'
                loading={loadingEditRoleRequest}
                disabled={loadingEditRoleRequest}
                onClick={submitModal}
              >
                Yes
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        {removedEntities.length > 0 && (
          <>
            The following items will be removed. Changes will take effect immediately.
            <ul>
              {removedEntities.map((e, index) => (
                <li key={index}>{e}</li>
              ))}
            </ul>
          </>
        )}
        {addedEntities.length > 0 && (
          <>
            The following items will be added. Changes will not take effect until the request has been approved.
            <ul>
              {addedEntities.map((e, index) => (
                <li key={index}>{e}</li>
              ))}
            </ul>
          </>
        )}
        Are you sure you want to submit?
      </Modal>
      <Modal
        visible={noPrincipalsModalVisible}
        header='Invalid edit'
        onDismiss={closeNoPrincipalsModal}
        footer={
          <Box float='right'>
            <Button variant='primary' onClick={closeNoPrincipalsModal}>
              Okay
            </Button>
          </Box>
        }
      >
        These edits cannot be submitted because a trust policy cannot have zero principals on it. First, submit your new
        principals and wait for them to be approved, and then remove the old principals.
      </Modal>
      <Modal
        visible={invalidArnsModalVisible}
        header='Invalid principals'
        onDismiss={closeInvalidArnsModal}
        footer={
          <Box float='right'>
            <Button variant='primary' onClick={closeInvalidArnsModal}>
              Okay
            </Button>
          </Box>
        }
      >
        The following principals are not valid:
        <ul>
          {invalidArns.map((e, index) => (
            <li key={index}>{e}</li>
          ))}
        </ul>
      </Modal>
      {(enableIamBasedAccess() || trustedEntitiesOld.length > 0 || trustedEntities.length > 0) && (
        <SpaceBetween size='l'>
          <Container
            header={
              <Header
                variant='h2'
                description={'Add IAM roles and users here to allow them to assume your Data Lake role.'}
                actions={
                  <SpaceBetween direction='horizontal' size='xs'>
                    {editingEntities ? (
                      <>
                        <Button onClick={cancel}>Cancel</Button>
                        <Button variant='primary' onClick={save} disabled={saveButtonDisabled()}>
                          Save
                        </Button>
                      </>
                    ) : (
                      <Button onClick={edit}>Edit</Button>
                    )}
                  </SpaceBetween>
                }
              >
                Trusted IAM principals
              </Header>
            }
          >
            {editingEntities ? (
              <AttributeEditor
                disableAddButton={trustedEntities.length === 25}
                empty='There are no trusted principals.'
                addButtonText='Add'
                removeButtonText='Remove'
                items={trustedEntities}
                definition={[
                  {
                    label: 'IAM Principal ARN',
                    control: (item, index) => <Input value={item} onChange={(e) => getOnInputHandler(e, index)} />,
                  },
                ]}
                onAddButtonClick={onAddButtonClickHandler}
                onRemoveButtonClick={onRemoveButtonClickHandler}
              />
            ) : // not editing and at least one principal -> display the principals in a table
            trustedEntities.length > 0 ? (
              <ColumnLayout columns={1} borders='horizontal'>
                {trustedEntities.map((e, index) => (
                  <div key={index}>{e}</div>
                ))}
              </ColumnLayout>
            ) : (
              // not editing and zero principals -> display an empty message
              <Box margin={{ vertical: 'xs' }} textAlign='center' color='inherit'>
                <SpaceBetween size='m'>
                  <b>No trusted principals</b>
                  <p>To add a principal, select "Edit" above.</p>
                </SpaceBetween>
              </Box>
            )}
          </Container>

          {pendingEntities.length > 0 && (
            <Container header={<Header variant='h2'>Trusted IAM principals pending approval</Header>}>
              <ColumnLayout columns={1} borders='horizontal'>
                {pendingEntities.map((e, index) => (
                  <div key={index}>{e}</div>
                ))}
              </ColumnLayout>
            </Container>
          )}
        </SpaceBetween>
      )}
    </div>
  );
};

export default TrustedIAMPrincipals;
